import { z } from 'zod';
import { IdSchema, DateTimeSchema, PaginationParamsSchema, HttpOrInternalFileUrlSchema, } from '../../common';
// ============================================
// Enums (wire-format — lowercase)
// ============================================
export const CourseKindSchema = z.enum(['group', 'private', 'camp']);
export const CourseStatusSchema = z.enum(['draft', 'active', 'passive', 'archived']);
export const DayOfWeekSchema = z.enum([
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
]);
export const BroadcastSystemSchema = z.enum(['system_internal', 'zoom']);
export const CourseContentTypeSchema = z.enum([
    'video_lesson',
    'webinar',
    'video',
    'document',
    'link',
    'vimeo',
    'live_lesson',
    'attendance',
    'iframe',
    'audio',
    'course_copy',
    'google_drive',
    'exam',
    'homework',
    // Düzeltme 5 · D5-4 — oluşturulan yaprak testleri eğitim içeriğine ekleme
    'worksheet',
]);
// Düzeltme 5 · D5-7 — yüklenebilir içerik alanları (video/document/audio) hem
// mutlak http(s) URL'i hem de "Dosya Yükle" sonucu dönen kök-göreli iç servis
// yolunu (`/api/v1/files/:id/serve`) kabul eder. Strict z.string().url() göreli
// yolu reddedip kaydı 400 ile düşürüyordu.
const UploadableUrlSchema = HttpOrInternalFileUrlSchema;
export const CampApplicationStatusSchema = z.enum(['pending', 'approved', 'rejected']);
// ============================================
// Course
// ============================================
export const CourseSchema = z.object({
    id: IdSchema,
    kind: CourseKindSchema,
    status: CourseStatusSchema,
    name: z.string(),
    description: z.string().nullable(),
    // PDF #5.1 — kapak görseli (önerilen 254×200)
    imageUrl: z.string().nullable().optional(),
    gradeLevel: z.number().int().min(1).max(12).nullable(),
    subjectId: IdSchema.nullable(),
    classSection: z.string().nullable(),
    teacherUserId: IdSchema.nullable(),
    dayOfWeek: DayOfWeekSchema.nullable(),
    startTime: z.string().nullable(), // "HH:mm"
    durationMin: z.number().int().nullable(),
    startsAt: DateTimeSchema.nullable(),
    endsAt: DateTimeSchema.nullable(),
    zoomMeetingId: z.string().nullable(),
    zoomPassword: z.string().nullable(),
    zoomLink: z.string().nullable(),
    hideHistoryFromLateJoiners: z.boolean(),
    isOngoing: z.boolean(),
    studentFee: z.number().nullable(),
    teacherFee: z.number().nullable(),
    sessionCount: z.number().int().nullable(),
    createdAt: DateTimeSchema,
    updatedAt: DateTimeSchema,
    // Web yayını (public tanıtım sitesi kataloğu) — admin liste/get için
    webPublished: z.boolean().optional(),
    webSummary: z.string().nullable().optional(),
    webSlug: z.string().nullable().optional(),
    webOrder: z.number().int().optional(),
    // Embedded summary fields (returned by list/get)
    enrollmentCount: z.number().int().optional(),
    contentCount: z.number().int().optional(),
    teacherName: z.string().nullable().optional(),
    subjectName: z.string().nullable().optional(),
    // Embedded students summary — admin private/group listesi için (öğrenci+veli).
    students: z
        .array(z.object({
        id: IdSchema,
        name: z.string(),
        parents: z
            .array(z.object({
            id: IdSchema,
            name: z.string(),
            relationship: z.string().nullable(),
            isPrimary: z.boolean(),
        }))
            .default([]),
    }))
        .optional(),
});
export const TimeOfDaySchema = z.string().regex(/^\d{2}:\d{2}$/, 'Expected HH:mm format');
export const CreateCourseSchema = z.object({
    kind: CourseKindSchema,
    // Düzeltme 9 · B1 — kayıt anında aktif/pasif seçimi. Gönderilmezse DB
    // varsayılanı (DRAFT) geçerli kalır; mevcut istemciler etkilenmez.
    status: CourseStatusSchema.optional(),
    name: z.string().min(1).max(160),
    description: z.string().max(4000).optional(),
    imageUrl: HttpOrInternalFileUrlSchema.optional().or(z.literal('')),
    gradeLevel: z.coerce.number().int().min(1).max(12).optional(),
    subjectId: IdSchema.optional(),
    classSection: z.string().max(20).optional(),
    teacherUserId: IdSchema.optional(),
    dayOfWeek: DayOfWeekSchema.optional(),
    startTime: TimeOfDaySchema.optional(),
    durationMin: z.coerce.number().int().min(5).max(600).optional(),
    startsAt: z.string().datetime().optional(),
    endsAt: z.string().datetime().optional(),
    zoomMeetingId: z.string().max(64).optional(),
    zoomPassword: z.string().max(64).optional(),
    // Görüntülü/canlı ders bağlantısı: zorunlu URL DEĞİL. Müşteri buraya kısa
    // toplantı kodu / serbest metin de girebiliyor. Strict z.string().url() kaydı
    // sessizce reddediyordu → serbest metne çevrildi. (Düzeltme 3 · D3-1)
    zoomLink: z.string().max(2000).optional(),
    hideHistoryFromLateJoiners: z.boolean().optional(),
    isOngoing: z.boolean().optional(),
    studentFee: z.coerce.number().nonnegative().optional(),
    teacherFee: z.coerce.number().nonnegative().optional(),
    sessionCount: z.coerce.number().int().min(1).max(20).optional(),
    // Initial enrolled students
    studentUserIds: z.array(IdSchema).optional(),
});
export const UpdateCourseSchema = CreateCourseSchema.partial().extend({
    status: CourseStatusSchema.optional(),
});
export const CourseQuerySchema = PaginationParamsSchema.extend({
    limit: z.coerce.number().int().positive().max(500).default(20),
    search: z.string().optional(),
    kind: CourseKindSchema.optional(),
    status: CourseStatusSchema.optional(),
    gradeLevel: z.coerce.number().int().min(1).max(12).optional(),
    subjectId: IdSchema.optional(),
    teacherUserId: IdSchema.optional(),
    hidePast: z.coerce.boolean().optional(),
});
// ============================================
// Enrollment
// ============================================
export const EnrollStudentsSchema = z.object({
    studentUserIds: z.array(IdSchema).min(1),
});
// ============================================
// CourseContent
// ============================================
export const CourseContentSchema = z.object({
    id: IdSchema,
    courseId: IdSchema,
    contentType: CourseContentTypeSchema,
    title: z.string(),
    description: z.string().nullable(),
    orderIndex: z.number().int(),
    unitId: IdSchema.nullable(),
    topicId: IdSchema.nullable(),
    videoUrl: z.string().nullable(),
    documentUrl: z.string().nullable(),
    linkUrl: z.string().nullable(),
    vimeoUrl: z.string().nullable(),
    iframeUrl: z.string().nullable(),
    audioUrl: z.string().nullable(),
    googleDriveUrl: z.string().nullable(),
    zoomMeetingId: z.string().nullable(),
    zoomPassword: z.string().nullable(),
    zoomLink: z.string().nullable(),
    repeatWeeks: z.number().int().nullable(),
    scoreMultiplier: z.number().nullable(),
    durationMin: z.number().int().nullable(),
    displayAt: DateTimeSchema.nullable(),
    // PDF #5.4.1 — canlı yayın / webinar alanları
    broadcastSystem: BroadcastSystemSchema.nullable(),
    broadcastDate: DateTimeSchema.nullable(),
    addAfterBroadcast: z.boolean(),
    translationEnabled: z.boolean(),
    examId: IdSchema.nullable(),
    homeworkId: IdSchema.nullable(),
    // Düzeltme 5 · D5-4 — eğitim içeriğine eklenen yaprak test referansı
    worksheetId: IdSchema.nullable(),
    createdAt: DateTimeSchema,
    updatedAt: DateTimeSchema,
});
export const CreateCourseContentSchema = z.object({
    contentType: CourseContentTypeSchema,
    title: z.string().min(1).max(200),
    description: z.string().max(4000).optional(),
    orderIndex: z.number().int().nonnegative().optional(),
    unitId: IdSchema.optional(),
    topicId: IdSchema.optional(),
    // Type-specific. video/document/audio "Dosya Yükle" ile iç servis yolu da
    // alabilir → UploadableUrlSchema (D5-7). link/vimeo/iframe/drive dış URL kalır.
    videoUrl: UploadableUrlSchema.optional(),
    documentUrl: UploadableUrlSchema.optional(),
    linkUrl: HttpOrInternalFileUrlSchema.optional(),
    vimeoUrl: HttpOrInternalFileUrlSchema.optional(),
    iframeUrl: HttpOrInternalFileUrlSchema.optional(),
    audioUrl: UploadableUrlSchema.optional(),
    googleDriveUrl: HttpOrInternalFileUrlSchema.optional(),
    zoomMeetingId: z.string().max(64).optional(),
    zoomPassword: z.string().max(64).optional(),
    // Görüntülü/canlı ders bağlantısı: zorunlu URL DEĞİL. Müşteri buraya kısa
    // toplantı kodu / serbest metin de girebiliyor. Strict z.string().url() kaydı
    // sessizce reddediyordu → serbest metne çevrildi. (Düzeltme 3 · D3-1)
    zoomLink: z.string().max(2000).optional(),
    repeatWeeks: z.coerce.number().int().min(0).max(52).optional(),
    scoreMultiplier: z.coerce.number().positive().optional(),
    durationMin: z.coerce.number().int().positive().optional(),
    displayAt: z.string().datetime().optional(),
    broadcastSystem: BroadcastSystemSchema.optional(),
    broadcastDate: z.string().datetime().optional(),
    addAfterBroadcast: z.boolean().optional(),
    translationEnabled: z.boolean().optional(),
    examId: IdSchema.optional(),
    homeworkId: IdSchema.optional(),
    // Düzeltme 5 · D5-4 — yaprak test içeriği referansı
    worksheetId: IdSchema.optional(),
});
export const UpdateCourseContentSchema = CreateCourseContentSchema.partial();
// ============================================
// CampApplication (public)
// ============================================
export const CampApplicationSchema = z.object({
    id: IdSchema,
    courseId: IdSchema,
    // Başvurulan kampın adı — admin listesinde gösterim için join'lenir.
    courseName: z.string().nullable().optional(),
    applicantName: z.string(),
    applicantEmail: z.string().email(),
    applicantPhone: z.string(),
    // Öğrencinin sınıf düzeyi + veli iletişim bilgileri (web şablonu düzeltme).
    gradeLevel: z.number().int().min(1).max(12).nullable(),
    parentName: z.string().nullable(),
    parentPhone: z.string().nullable(),
    notes: z.string().nullable(),
    status: CampApplicationStatusSchema,
    reviewerId: IdSchema.nullable(),
    reviewedAt: DateTimeSchema.nullable(),
    approvedStudentUserId: IdSchema.nullable(),
    createdAt: DateTimeSchema,
});
function campPhoneDigits(value) {
    return value.replace(/\D/g, '');
}
export const CreateCampApplicationSchema = z
    .object({
    courseId: IdSchema,
    applicantName: z.string().min(2).max(120),
    applicantEmail: z.string().email(),
    applicantPhone: z.string().min(7).max(20),
    gradeLevel: z.coerce.number().int().min(1).max(12),
    parentName: z.string().min(2).max(120),
    parentPhone: z.string().min(7).max(20),
    notes: z.string().max(1000).optional(),
})
    .superRefine((data, ctx) => {
    const student = campPhoneDigits(data.applicantPhone);
    const parent = campPhoneDigits(data.parentPhone);
    if (student && parent && student === parent) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['parentPhone'],
            message: 'Öğrenci ve veli telefon numaraları aynı olamaz. Lütfen farklı numaralar girin.',
        });
    }
});
export const ReviewCampApplicationSchema = z.object({
    status: z.enum(['approved', 'rejected']),
    reason: z.string().max(500).optional(),
});
export const ApplyCampSchema = z.object({
    courseId: IdSchema,
    studentId: IdSchema.optional(),
    notes: z.string().max(1000).optional(),
});
export const CampApplicationQuerySchema = PaginationParamsSchema.extend({
    courseId: IdSchema.optional(),
    status: CampApplicationStatusSchema.optional(),
    gradeLevel: z.coerce.number().int().min(1).max(12).optional(),
});
// ============================================
// CampSession
// ============================================
export const CampSessionSchema = z.object({
    id: IdSchema,
    courseId: IdSchema,
    sessionIndex: z.number().int().min(1).max(20),
    startsAt: DateTimeSchema,
    durationMin: z.number().int().min(5).max(600),
    createdAt: DateTimeSchema,
});
export const CreateCampSessionSchema = z.object({
    sessionIndex: z.coerce.number().int().min(1).max(20),
    startsAt: z.string().datetime(),
    durationMin: z.coerce.number().int().min(5).max(600),
});
export const UpdateCampSessionSchema = CreateCampSessionSchema.partial();
// Bulk-create N camp sessions in a single transaction (form submit flow).
export const CreateCampSessionsBulkSchema = z.object({
    sessions: z.array(CreateCampSessionSchema).min(1).max(20),
});
// ============================================
// Web yayını (public tanıtım sitesi — Eğitimler kataloğu)
// ============================================
// Admin: bir eğitimi web'de yayınla/çıkar + özet/slug/sıra düzenle.
export const UpdateCourseWebSchema = z.object({
    webPublished: z.boolean().optional(),
    webSummary: z.string().max(500).nullable().optional(),
    webSlug: z
        .string()
        .min(1)
        .max(120)
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Yalnızca küçük harf, rakam ve tire')
        .nullable()
        .optional(),
    webOrder: z.coerce.number().int().min(0).max(9999).optional(),
});
export const PublicCourseSessionSchema = z.object({
    sessionIndex: z.number().int().min(1),
    startsAt: DateTimeSchema,
    durationMin: z.number().int().min(5).max(600),
});
// Public: katalog kartı / detay (yalnızca yayınlanmış alanlar).
export const PublicCourseSchema = z.object({
    id: IdSchema,
    slug: z.string(),
    name: z.string(),
    kind: CourseKindSchema,
    gradeLevel: z.number().int().nullable(),
    subjectName: z.string().nullable(),
    teacherName: z.string().nullable(),
    teacherPhotoUrl: z.string().nullable(),
    teacherBranch: z.string().nullable(),
    imageUrl: z.string().nullable(),
    summary: z.string().nullable(),
    description: z.string().nullable(),
    studentFee: z.number().nullable(),
    sessionCount: z.number().int().nullable(),
    dayOfWeek: DayOfWeekSchema.nullable(),
    startTime: z.string().nullable(),
    durationMin: z.number().int().nullable(),
    sessions: z.array(PublicCourseSessionSchema),
});
export const PublicCourseListSchema = z.object({
    items: z.array(PublicCourseSchema),
    total: z.number().int().nonnegative(),
});
export const PublicCourseQuerySchema = z.object({
    kind: CourseKindSchema.optional(),
});
//# sourceMappingURL=course.js.map